// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { Link } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import { createFilter } from 'react-select';

import { useEntityPermissions } from '@gandi/acl';
import { selectIsSamlUser } from '@gandi/authentication';
import {
  selectCartUUID as selectCartId,
  selectCartProductOwner,
  setCartProductOwner,
  setCartReselleeId,
  setCartOwnerIsReseller,
} from '@gandi/cart';
import {
  type Organization,
  fetchOrganizationsList as fetchCandidates,
  fetchOneOrganization as fetchCandidate,
  selectOrganizationsList,
  selectIsFetchingOrganizations,
  selectIsFailureOrganizations,
  selectIsLoadedOrganizations,
  selectIsToRefetchOrganizations,
} from '@gandi/redux-organization';

import type { Organization as OrganizationSharing } from '@gandi/redux-sharing';

import { useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';
import { Custom } from '@gandi/ui-select';

import { useAppSelector } from '../../../hooks/useAppSelector';
import { selectShopCurrent } from '../../../modules/organization/selectors';
import { ORGA_WRITE } from '../../../modules/permissions';

import type {
  ComponentWithPrefetch,
  FullState,
  PreFetchArgs,
} from '../../../types';

import { Option } from './Option';

import styles from './PayerSelect.scss';

type OwnProps = {
  frozen: boolean,
};

export type Props = {
  ...OwnProps,
};

function stringify({ data }: any): string {
  return Object.keys(data)
    .filter(key => data[key] && typeof data[key] === 'string')
    .reduce((acc, key) => {
      // default case
      let substring = data[key];

      // Handle firstname / lastname semantic groups
      if (key === 'firstname') {
        substring = `${acc}${data.firstname} ${data.lastname}`;
      }
      if (key === 'lastname') {
        substring = `${acc}${data.lastname} ${data.firstname}`;
      }

      return `${acc}${substring} `;
    }, '');
}

function getOrgEdit(org: string, cart: string) {
  return `/cart/${cart}/configure/account/${org}?owner=products`;
}

function getId(org: any): string {
  return org.id || '';
}

function getDisplayName(org: Organization | OrganizationSharing): string {
  return org.name || org.orgname || '';
}

function renderOption(org: Organization): React.Node {
  return <Option key={org.id} org={org} />;
}

function onChange(
  callback: (id: string) => void,
  current: void | Organization | OrganizationSharing,
  update: (next: void | Organization | OrganizationSharing) => any,
  next_: any,
) {
  const next = Array.isArray(next_) ? next_[0] : next_;
  if (next !== current) {
    update(next);
    if (typeof callback === 'function') {
      callback(next.id);
    }
  }
}

export function OwnerSelect({ frozen }: Props): React.Node {
  const translator = useTranslator();
  const { __ } = translator;
  const dispatch = useDispatch();

  const [mounted, setMounted] = React.useState(false);

  const cartId = useAppSelector(selectCartId);
  const currentOrg = useAppSelector(state => selectShopCurrent(state, true));
  const candidates = useAppSelector(selectOrganizationsList);
  const loadedCandidates = useAppSelector(selectIsLoadedOrganizations);
  const fetchingCandidates = useAppSelector(selectIsFetchingOrganizations);
  const failureCandidates = useAppSelector(selectIsFailureOrganizations);
  const refetchChandidates = useAppSelector(selectIsToRefetchOrganizations);
  const productOwner = useAppSelector(selectCartProductOwner);
  const isSamlUser = useAppSelector(selectIsSamlUser);

  /* --------------- Force a re-render after hydration/mount. --------------- */
  // Required to mitigate a SSR/hydration bug in Safari
  React.useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, [mounted, setMounted]);
  /* ------------------------------------------------------------------------ */

  /* ----------- Reset possibly conflicting cart ownership state. ----------- */
  // Required to prevent possible desync cases when user selects a product owner
  // for the cart, then change their mind a select a customer.
  React.useEffect(() => {
    dispatch(setCartReselleeId(null));
  }, [dispatch]);
  /* ------------------------------------------------------------------------ */

  React.useEffect(() => {
    dispatch(setCartOwnerIsReseller(null));
  }, [dispatch]);

  const defaultOwner: Organization | OrganizationSharing | void = productOwner
    ? candidates.find(candidate => candidate.id === productOwner) || undefined
    : currentOrg;

  const [owner, set] = React.useState<
    (Organization | OrganizationSharing) | void,
  >(defaultOwner);

  const { hasPermissions, isLoadedPermissions, isFetchingPermissions } =
    useEntityPermissions(owner?.id || 'no-owner-yet');

  const shouldFetch =
    !fetchingCandidates &&
    ((!loadedCandidates && !failureCandidates) || refetchChandidates);

  React.useEffect(() => {
    if (shouldFetch) {
      dispatch(fetchCandidates()).then(response => {
        if (
          Array.isArray(response.data) &&
          productOwner &&
          defaultOwner?.id !== productOwner
        ) {
          const match = response.data.find(
            candidate => candidate.id === productOwner,
          );
          if (match) {
            set(match);
          }
        }
      });
    }
    // Only dependency change that should trigger this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = React.useCallback(
    onChange.bind(
      null,
      (orgId: string) => {
        /// XXX: We should probably move this logix to useApplyInvoicedOrga
        dispatch(setCartProductOwner(orgId));
        dispatch(fetchCandidate(orgId));
      },
      owner,
      set,
    ),
    [owner, set],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterOpt = React.useCallback(createFilter({ stringify }), []);

  const renderNoOpt = React.useCallback(
    () => __('Only organizations eligible for this order are listed'),
    [__],
  );

  return (
    <Base flex flexCol>
      <div className={styles.container}>
        <Custom
          id="owner-select-container"
          inputId="owner-select-input"
          instanceId="owner-select-instance"
          translator={translator}
          name="owner"
          isClearable={false}
          isDisabled={!mounted || frozen || fetchingCandidates}
          value={defaultOwner}
          options={candidates}
          getOptionValue={getId}
          filterOption={filterOpt}
          renderOption={renderOption}
          onChange={handleChange}
          maxMenuHeight={500}
          noOptionsMessage={renderNoOpt}
        />
      </div>
      <span className={styles.linksContainer}>
        {owner &&
          !isFetchingPermissions &&
          isLoadedPermissions &&
          hasPermissions([ORGA_WRITE]) && (
            <>
              <Link to={getOrgEdit(owner.id, cartId)}>
                {translator.__('Edit %(name)s', {
                  name: getDisplayName(owner),
                })}
              </Link>
              {!isSamlUser && <hr className={styles.separator} />}
            </>
          )}
        {!isSamlUser && (
          <Link to={`/cart/${cartId}/configure/account/new?owner=products`}>
            {__('Create New')}
          </Link>
        )}
      </span>
    </Base>
  );
}

OwnerSelect.preFetch = ({
  dispatch,
}: PreFetchArgs<{}, any, any, FullState>): Promise<any> =>
  dispatch(fetchCandidates());

export default (OwnerSelect: ComponentWithPrefetch<
  OwnProps,
  typeof OwnerSelect.preFetch,
>);
